import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, take } from 'rxjs';
import { LayersStore } from '../common/current-layers/layers-store.service';
import { JobFeatureDetailType, JobFeatureService } from '../map-data-services/feature/job-feature-cache.service';
import { SurveyJob, SurveyJobAttachment } from './survey-job';
import { SurveyJobService } from './survey-job.services';

export interface JobCache {
    job: SurveyJob | null;
    attachments: SurveyJobAttachment[];
    linkedData: SurveyJobAttachment[];
    features: JobFeatureDetailType | null;
    isLoading: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SurveyJobPreloadService {
    private jobCache = new Map<string, JobCache>();
    private preloadQueue = new Set<string>();
    private isProcessingQueue = false;
    public currentProjectId: string;

    public reloadJobListStream = new BehaviorSubject<boolean>(false);

    constructor(
        private surveyJobService: SurveyJobService,
        private jobFeatureService: JobFeatureService,
        private layersStore: LayersStore
    ) {}

    /**
     * Queue jobs for preloading based on visible items in list
     * Note: User-selected jobs (via getJobData$) are automatically prioritized
     * and removed from this background queue to ensure faster loading
     * @param jobs Array of job objects with id and eTag to preload
     */
    queueJobsForPreloading(jobs: { id: string; eTag: string }[]): void {
        // Add new jobs to queue
        jobs.forEach(job => {
            if (!this.jobCache.has(job.id)) {
                this.preloadQueue.add(job.id);
            } else if (this.jobCache.has(job.id)) {
                const jobCache = this.jobCache.get(job.id);
                if (jobCache?.job?.eTag !== job.eTag) {
                    this.preloadQueue.add(job.id);
                }
            }
        });

        // Start processing if not already in progress
        if (!this.isProcessingQueue) {
            this.processPreloadQueue();
        }
    }

    /**
     * Process the preload queue with limited concurrency
     */
    private processPreloadQueue(): void {
        if (this.preloadQueue.size === 0) {
            this.isProcessingQueue = false;
            return;
        }

        this.isProcessingQueue = true;

        // Get next job ID from queue
        const jobId = this.preloadQueue.values().next().value;
        this.preloadQueue.delete(jobId);

        // Initialize cache entry
        this.jobCache.set(jobId, {
            job: null,
            attachments: [],
            linkedData: [],
            features: null,
            isLoading: true
        });

        // Start preloading (using false to not return JobCache from queue processing)
        this.ensureLayersAndLoadJobData(jobId, false).finally(() => {
            // Process next item in queue
            setTimeout(() => this.processPreloadQueue(), 0);
        });
    }

    /**
     * Get cached job data if available, or undefined if not cached
     */
    getCachedJobData(jobId: string): JobCache | undefined {
        return this.jobCache.get(jobId);
    }

    /**
     * Get cached job data from cache service otherwise load from api
     * @param jobId The ID of the job to get data for
     * @param isUserSelected If true, prioritizes this job over background preloading (default: true)
     * @returns Promise that resolves with the JobCache
     */
    getSurveyJobByIdFromCacheService(jobId: string, isUserSelected: boolean = true): Promise<JobCache> {
        if (this.jobCache.has(jobId)) {
            return Promise.resolve(this.jobCache.get(jobId));
        } else {
            return this.getJobData$(jobId, isUserSelected);
        }
    }

    /**
     * Get job data, forcing a refresh if not available in cache
     * User-selected jobs get priority over background preloading
     * @param jobId The ID of the job to get data for
     * @param isUserSelected If true, removes job from background queue to prioritize it
     * @returns Promise that resolves with the loaded JobCache
     */
    getJobData$(jobId: string, isUserSelected: boolean = true): Promise<JobCache> {
        // If user selected this job, remove it from the preload queue to prioritize it
        if (isUserSelected && this.preloadQueue.has(jobId)) {
            this.preloadQueue.delete(jobId);
        }

        // For user-selected jobs, load data immediately without waiting for layers
        // Features will load after layers are ready, but other data loads immediately
        if (isUserSelected) {
            return this.loadJobDataWithPriority(jobId);
        }

        return this.freshJobData(jobId, true) as Promise<JobCache>;
    }

    /**
     * Check if job data is fully cached
     */
    isJobFullyCached(jobId: string): boolean {
        const cacheEntry = this.jobCache.get(jobId);
        return !!cacheEntry && !cacheEntry.isLoading && !!cacheEntry.job;
    }

    /**
     * Clear cached data for a specific job
     */
    clearCache(jobId: string): void {
        this.jobCache.delete(jobId);
    }

    /**
     * Update the job in cache and optionally reload linked data.
     * @param updatedJob The updated SurveyJob object.
     * @param isLinkedDataUpdated Whether linked data should be refreshed.
     * @returns Promise that resolves with the updated JobCache or undefined.
     */
    async updateJobInCache(updatedJob: SurveyJob, isLinkedDataUpdated: boolean): Promise<JobCache | undefined> {
        try {
            const currentCache = this.jobCache.get(updatedJob.id);
            if (currentCache) {
                this.jobCache.set(updatedJob.id, { ...currentCache, job: updatedJob });
                if (isLinkedDataUpdated) {
                    await this.loadLinkedProjectData(updatedJob.id);
                }
            } else {
                await this.loadJobData(updatedJob.id, true);
            }
            return Promise.resolve(this.jobCache.get(updatedJob.id));
        } catch (error) {
            console.error('Error updating job:', error);
            return;
        }
    }

    /**
     * Loads linked map files for the current job
     */
    private async loadLinkedProjectData(jobId: string, includeDownloadUrl = true): Promise<void> {
        const response = await this.surveyJobService.getLinkedProjectData(jobId, includeDownloadUrl);
        this.jobCache.set(jobId, {
            ...this.jobCache.get(jobId),
            linkedData: response.items
        });
    }

    /**
     * Force fresh job data by making fresh API calls
     * @param jobId The ID of the job to fresh data for
     * @param returnJobCache If true, returns the JobCache object directly
     * @returns Promise that resolves when all data is loaded, with the JobCache if returnJobCache is true
     */
    freshJobData(jobId: string, returnJobCache: boolean = false): Promise<void | JobCache> {
        // Initialize cache entry
        this.jobCache.set(jobId, {
            job: null,
            attachments: [],
            linkedData: [],
            features: null,
            isLoading: true
        });

        return this.ensureLayersAndLoadJobData(jobId, returnJobCache);
    }

    /**
     * Clear entire cache
     */
    clearAllCache(): void {
        this.jobCache.clear();
    }
    /**
     * Load job data with priority for user-selected jobs
     * Loads job info, attachments, and linked data immediately
     * Features are loaded in background after layers are ready
     * @param jobId The ID of the job to load data for
     * @returns Promise that resolves with the loaded JobCache
     */
    private async loadJobDataWithPriority(jobId: string): Promise<JobCache> {
        // Initialize cache entry
        this.jobCache.set(jobId, {
            job: null,
            attachments: [],
            linkedData: [],
            features: null,
            isLoading: true
        });

        try {
            // Load job info, attachments, and linked data immediately (don't wait for layers)
            const [job, attachments, linkedData] = await Promise.all([
                this.surveyJobService.getSurveyJobById(jobId, false),
                this.surveyJobService.getSurveyJobAttachments(jobId, true),
                this.surveyJobService.getLinkedProjectData(jobId, true)
            ]);

            // Update cache with partial data immediately
            const partialCache: JobCache = {
                job,
                attachments: attachments.items,
                linkedData: linkedData.items,
                features: null,
                isLoading: false // Mark as not loading so UI can display partial data
            };

            this.jobCache.set(jobId, partialCache);

            // Load features in background after layers are ready
            this.loadFeaturesInBackground(jobId);

            return partialCache;
        } catch (error) {
            const currentCache = this.jobCache.get(jobId);
            if (currentCache) {
                this.jobCache.set(jobId, { ...currentCache, isLoading: false });
            }
            throw error;
        }
    }

    /**
     * Load features in background after layers are ready
     * @param jobId The ID of the job to load features for
     */
    private async loadFeaturesInBackground(jobId: string): Promise<void> {
        try {
            // Wait for layers to be ready
            await this.layersStore.mapWorkspaceLayersLoadingStream
                .pipe(
                    filter(val => !val),
                    take(1)
                )
                .toPromise();

            // Load features
            const features = await this.jobFeatureService.getFeaturesDetail(jobId).toPromise();

            // Update cache with features
            const currentCache = this.jobCache.get(jobId);
            if (currentCache) {
                this.jobCache.set(jobId, {
                    ...currentCache,
                    features
                });
            }
        } catch (error) {
            // Silently handle error
        }
    }

    /**
     * Loads job data after ensuring map workspace layers are loaded.
     * @param jobId The ID of the job to load data for
     * @returns Promise that resolves with the loaded JobCache
     */
    private async ensureLayersAndLoadJobData(jobId: string, returnJobCache: boolean = false): Promise<void | JobCache> {
        await this.layersStore.mapWorkspaceLayersLoadingStream
            .pipe(
                filter(val => !val),
                take(1)
            )
            .toPromise();

        return this.loadJobData(jobId, returnJobCache);
    }

    /**
     * Load job data from APIs and update the cache
     * @param jobId The ID of the job to load data for
     * @param notFromQueue If true, returns the JobCache object directly
     * @returns Promise that resolves when all data is loaded, with the JobCache if notFromQueue is true
     */
    private loadJobData(jobId: string, notFromQueue: boolean = false): Promise<void | JobCache> {
        return Promise.all([
            this.surveyJobService.getSurveyJobById(jobId, false),
            this.surveyJobService.getSurveyJobAttachments(jobId, true),
            this.surveyJobService.getLinkedProjectData(jobId, true),
            this.jobFeatureService.getFeaturesDetail(jobId).toPromise()
        ])
            .then(([job, attachments, linkedData, features]) => {
                const jobCache: JobCache = {
                    job,
                    attachments: attachments.items,
                    linkedData: linkedData.items,
                    features,
                    isLoading: false
                };

                this.jobCache.set(jobId, jobCache);

                // Return the job cache if requested
                if (notFromQueue) {
                    return jobCache;
                }
            })
            .catch(error => {
                // On error, mark as not loading but keep whatever data we have
                const currentCache = this.jobCache.get(jobId);
                if (currentCache) {
                    this.jobCache.set(jobId, { ...currentCache, isLoading: false });
                }

                // Rethrow the error if this was a direct call
                if (notFromQueue) {
                    throw error;
                }
            });
    }
}
